import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { IconCamera, IconTrash } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { LoadingImage } from "@/components/loading-image";
import { ResponsiveModal } from "@/components/responsive-modal";
import { Skeleton } from "@/components/ui/skeleton";
import { PhotoCategoryGrid } from "@/components/photo-category-grid";
import { PhotoUploadDrawer } from "@/components/photo-upload-drawer";
import { usePhotoCategories } from "@/api/hooks";
import {
  useDeleteWeighingPhoto,
  useWeighingPhotos,
  type WeighingPhoto,
} from "@/api/hooks/use-weighing-photos";
import { enqueueUpload, useUploadingCategoryIds } from "@/lib/upload-store";
import { flattenCategories, getWeighingSections } from "@/lib/photo-categories";
import type { PhotoCategoryId, PhotoId, PlatformWeighingId } from "@/types/identifiers";

interface WeighingPhotosSectionProps {
  weighingId: PlatformWeighingId;
  readOnly?: boolean;
}

/** Categorized photo evidence of a platform weighing (owned by the weighing,
 *  not a build — weighings are boat-scoped and cross-event). */
export function WeighingPhotosSection({ weighingId, readOnly = false }: WeighingPhotosSectionProps) {
  const { data: photos, isLoading: photosLoading } = useWeighingPhotos(weighingId);
  const { data: allCategories, isLoading: catsLoading } = usePhotoCategories();
  const sections = getWeighingSections(allCategories);
  const [openCategoryId, setOpenCategoryId] = useState<PhotoCategoryId | null>(null);

  // In-flight uploads (tracked by the global tray) drive the per-slot spinner.
  const uploadingCategoryIds = useUploadingCategoryIds(weighingId);

  const isLoading = photosLoading || catsLoading;

  const allLeaves = flattenCategories(sections);

  const photosByCategoryId = new Map<PhotoCategoryId, WeighingPhoto[]>();
  for (const p of photos ?? []) {
    const arr = photosByCategoryId.get(p.category_id);
    if (arr) arr.push(p);
    else photosByCategoryId.set(p.category_id, [p]);
  }

  if (isLoading) {
    return (
      <div className="space-y-6 mt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {Array.from({ length: 4 }).map((_, j) => (
                <Skeleton key={j} className="aspect-[4/3] rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const activeCategory = allLeaves.find((c) => c.id === openCategoryId);
  const activePhotos = openCategoryId ? (photosByCategoryId.get(openCategoryId) ?? []) : [];

  return (
    <>
      <div className="mt-4">
        <PhotoCategoryGrid
          sections={sections}
          photosByCategoryId={photosByCategoryId}
          onSlotClick={readOnly ? undefined : setOpenCategoryId}
          uploadingCategoryIds={uploadingCategoryIds}
        />
      </div>

      {activeCategory && (
        activeCategory.allow_multiple ? (
          <MultiPhotoDrawer
            open={!!openCategoryId}
            onOpenChange={(open) => { if (!open) setOpenCategoryId(null); }}
            weighingId={weighingId}
            categoryId={activeCategory.id}
            label={activeCategory.name}
            photos={activePhotos}
          />
        ) : (
          <WeighingPhotoDrawer
            open={!!openCategoryId}
            onOpenChange={(open) => { if (!open) setOpenCategoryId(null); }}
            weighingId={weighingId}
            categoryId={activeCategory.id}
            label={activeCategory.name}
            photo={activePhotos[0]}
          />
        )
      )}
    </>
  );
}

// Single-photo slot: capturing hands the file to the upload tray and closes the
// dialog immediately, so the operator can move straight to the next category.

interface WeighingPhotoDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weighingId: PlatformWeighingId;
  categoryId: PhotoCategoryId;
  label: string;
  photo: WeighingPhoto | undefined;
}

function WeighingPhotoDrawer({
  open,
  onOpenChange,
  weighingId,
  categoryId,
  label,
  photo,
}: WeighingPhotoDrawerProps) {
  const remove = useDeleteWeighingPhoto(weighingId);

  const handleFile = useCallback((file: File) => {
    enqueueUpload({ weighingId, categoryId, label, file });
    onOpenChange(false);
  }, [weighingId, categoryId, label, onOpenChange]);

  const handleDelete = useCallback(() => {
    if (!photo) return;
    remove.mutate(photo.id, {
      onSuccess: () => {
        toast.success("Photo deleted");
        onOpenChange(false);
      },
      onError: (err) => toast.error(err.message),
    });
  }, [photo, remove, onOpenChange]);

  return (
    <PhotoUploadDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={label}
      photoUrl={photo?.image_url}
      isUploading={false}
      uploadProgress={0}
      isDeleting={remove.isPending}
      justUploaded={false}
      hasPhoto={!!photo}
      onFileSelected={handleFile}
      onDelete={handleDelete}
    />
  );
}

// Multi-photo category: each pick is handed to the tray and the drawer stays
// open, so several can be added in a row without waiting.

interface MultiPhotoDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weighingId: PlatformWeighingId;
  categoryId: PhotoCategoryId;
  label: string;
  photos: WeighingPhoto[];
}

function MultiPhotoDrawer({
  open,
  onOpenChange,
  weighingId,
  categoryId,
  label,
  photos,
}: MultiPhotoDrawerProps) {
  const remove = useDeleteWeighingPhoto(weighingId);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    enqueueUpload({ weighingId, categoryId, label, file, allowMultiple: true });
    e.target.value = "";
  };

  const handleDelete = useCallback((photoId: PhotoId) => {
    remove.mutate(photoId, {
      onError: (err) => toast.error(err.message),
    });
  }, [remove]);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={label}
    >
      <div className="flex flex-col gap-4">
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => {
              const src = photo.thumbnail_url ?? photo.image_url;
              return (
                <div key={photo.id} className="relative group">
                  {src && (
                    <LoadingImage
                      src={src}
                      alt={label}
                      className="aspect-[4/3] w-full object-cover rounded-lg border border-border"
                      skeletonClassName="aspect-[4/3] rounded-lg"
                    />
                  )}
                  <button
                    onClick={() => handleDelete(photo.id)}
                    disabled={remove.isPending}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/50 flex items-center justify-center text-white transition-colors hover:bg-black/70"
                  >
                    <IconTrash className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
        >
          <IconCamera className="mr-1.5 h-4 w-4" />
          Add Photo
        </Button>
        <p className="text-[11px] text-muted-foreground text-center">
          Uploads continue in the tray — add as many as you like.
        </p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </ResponsiveModal>
  );
}
